import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card,
  Button,
  Space,
  Input,
  Modal,
  Tabs,
  Tag,
  Select,
  Switch,
  Alert,
  Tooltip,
  Spin,
  Empty,
  Divider,
  Checkbox,
} from 'antd';
import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  GlobalOutlined,
  CodeOutlined,
  TableOutlined,
  ReloadOutlined,
  SaveOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { getRemoteConfig, getRemoteConfigs } from '../../graphql/queries';
import {
  updateRemoteConfigName,
  deleteRemoteConfig,
  upsertRemoteConfigLocale,
  deleteRemoteConfigLocale,
  translateRemoteConfigContent,
} from '../../graphql/mutation';
import JsonEditor from '../../components/JsonEditor/JsonEditor';
import { POPULAR_LOCALES, LOCALE_REGEX, getLocaleLabel } from '../../utils/locales';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './RemoteConfigDetail.css';

const DEFAULT_LOCALE = 'en';
const ROW_TYPES = ['string', 'number', 'boolean', 'array', 'object', 'null'];

const newId = () => Math.random().toString(36).slice(2, 10);

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const parseJson = (text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return { ok: false, error: 'Content cannot be empty' };
  try {
    return { ok: true, data: JSON.parse(trimmed) };
  } catch (e) {
    return { ok: false, error: e.message || 'Invalid JSON' };
  }
};

const isPlainObject = (v) =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const detectType = (v) => {
  if (v === null) return 'null';
  if (typeof v === 'string') return 'string';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'boolean';
  if (Array.isArray(v)) return 'array';
  return 'object';
};

const valueToRowString = (v, type) => {
  if (type === 'null') return '';
  if (type === 'string') return String(v ?? '');
  if (type === 'number') return String(v ?? 0);
  if (type === 'boolean') return v ? 'true' : 'false';
  // array / object — stringified JSON
  return JSON.stringify(v ?? (type === 'array' ? [] : {}), null, 2);
};

const objectToRows = (obj) =>
  Object.entries(obj).map(([key, value]) => {
    const type = detectType(value);
    return { id: newId(), key, type, value: valueToRowString(value, type) };
  });

const rowToValue = (row) => {
  if (row.type === 'string') return { ok: true, data: row.value };
  if (row.type === 'number') {
    const n = Number(row.value);
    if (Number.isNaN(n)) return { ok: false, error: 'not a valid number' };
    return { ok: true, data: n };
  }
  if (row.type === 'boolean') return { ok: true, data: row.value === 'true' };
  if (row.type === 'null') return { ok: true, data: null };
  if (row.type === 'array') {
    try {
      const parsed = JSON.parse(row.value);
      if (!Array.isArray(parsed)) return { ok: false, error: 'value is not a JSON array' };
      return { ok: true, data: parsed };
    } catch (e) {
      return { ok: false, error: `invalid JSON array (${e.message})` };
    }
  }
  try {
    const parsed = JSON.parse(row.value);
    if (!isPlainObject(parsed)) return { ok: false, error: 'value is not a JSON object' };
    return { ok: true, data: parsed };
  } catch (e) {
    return { ok: false, error: `invalid JSON object (${e.message})` };
  }
};

const rowsToObject = (rows) => {
  const obj = {};
  const seen = new Set();
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) return { ok: false, error: 'Every row needs a key.' };
    if (seen.has(key)) return { ok: false, error: `Duplicate key "${key}".` };
    seen.add(key);
    const v = rowToValue(row);
    if (!v.ok) return { ok: false, error: `"${key}" → ${v.error}` };
    obj[key] = v.data;
  }
  return { ok: true, data: obj };
};

const defaultValueForType = (type) => {
  if (type === 'boolean') return 'false';
  if (type === 'number') return '0';
  if (type === 'array') return '[]';
  if (type === 'object') return '{}';
  return '';
};

const makeEditState = (content) => ({
  jsonText: JSON.stringify(content ?? {}, null, 2),
  rows: isPlainObject(content) ? objectToRows(content) : [],
});

const readContent = (editState, view) => {
  if (view === 'json') return parseJson(editState.jsonText);
  return rowsToObject(editState.rows);
};

const deepEqualJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/* ============================================================== */
/* KvTable — flat typed key/value editor                           */
/* ============================================================== */

const KvTable = ({ rows, onChange }) => {
  const addRow = () => {
    onChange([
      ...rows,
      { id: newId(), key: '', type: 'string', value: '' },
    ]);
  };
  const updateRow = (id, patch) => {
    onChange(
      rows.map((r) => {
        if (r.id !== id) return r;
        const merged = { ...r, ...patch };
        if (patch.type && patch.type !== r.type) {
          merged.value = defaultValueForType(patch.type);
        }
        return merged;
      }),
    );
  };
  const removeRow = (id) => onChange(rows.filter((r) => r.id !== id));

  return (
    <div>
      {rows.length === 0 ? (
        <Empty
          description='No keys yet. Click "Add row" below, or switch to JSON view.'
          style={{ padding: 24 }}
        />
      ) : (
        <div className="kv-table">
          <div className="kv-row kv-header">
            <div>Key</div>
            <div>Type</div>
            <div>Value</div>
            <div />
          </div>
          {rows.map((row) => (
            <div className="kv-row" key={row.id}>
              <Input
                style={{ fontFamily: 'monospace' }}
                placeholder="key"
                value={row.key}
                onChange={(e) => updateRow(row.id, { key: e.target.value })}
              />
              <Select
                value={row.type}
                onChange={(v) => updateRow(row.id, { type: v })}
                options={ROW_TYPES.map((t) => ({ value: t, label: t }))}
              />
              <RowValueCell row={row} onValueChange={(value) => updateRow(row.id, { value })} />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeRow(row.id)}
              />
            </div>
          ))}
        </div>
      )}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addRow}
        style={{ marginTop: 12 }}
      >
        Add row
      </Button>
    </div>
  );
};

const RowValueCell = ({ row, onValueChange }) => {
  if (row.type === 'null') {
    return (
      <span style={{ alignSelf: 'center', fontFamily: 'monospace', color: 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}>
        null
      </span>
    );
  }
  if (row.type === 'object' || row.type === 'array') {
    return (
      <Input.TextArea
        rows={4}
        style={{ fontFamily: 'monospace' }}
        value={row.value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={row.type === 'array' ? '[]' : '{}'}
      />
    );
  }
  if (row.type === 'boolean') {
    return (
      <Switch
        checked={row.value === 'true'}
        onChange={(c) => onValueChange(c ? 'true' : 'false')}
        checkedChildren="true"
        unCheckedChildren="false"
      />
    );
  }
  return (
    <Input
      type={row.type === 'number' ? 'number' : 'text'}
      value={row.value}
      onChange={(e) => onValueChange(e.target.value)}
    />
  );
};

/* ============================================================== */
/* LocaleEditor — lifted state, no per-locale Save                 */
/* ============================================================== */

const LocaleEditor = ({
  locale,
  editState,
  onEditStateChange,
  view,
  onViewChange,
  enContent,
  status,
  initialUpdatedAt,
  dirty,
  onReset,
  onMarkDelete,
  onDiscardAdd,
  onApplyTranslation,
}) => {
  const isPendingAdd = status === 'pending-add';
  const isDefault = locale === DEFAULT_LOCALE;
  const canRetranslate = locale !== DEFAULT_LOCALE;

  const [viewError, setViewError] = useState(null);
  const [retranslating, setRetranslating] = useState(false);
  const [retranslateError, setRetranslateError] = useState(null);
  const [translateMutation] = useMutation(translateRemoteConfigContent);

  const jsonParseError = useMemo(() => {
    if (view !== 'json') return null;
    const r = parseJson(editState.jsonText);
    return r.ok ? null : r.error;
  }, [editState.jsonText, view]);

  const handleRetranslate = async () => {
    if (!canRetranslate || !onApplyTranslation) return;
    setRetranslateError(null);
    setRetranslating(true);
    try {
      const result = await translateMutation({
        variables: {
          sourceContent: enContent,
          sourceLocale: 'en',
          targetLocales: [locale],
        },
      });
      const item = result.data?.translateRemoteConfigContent?.[0];
      if (!item) {
        setRetranslateError('Translation returned no result.');
        return;
      }
      if (item.error || item.content == null) {
        setRetranslateError(item.error ?? 'Translation failed.');
        return;
      }
      onApplyTranslation(item.content);
    } catch (e) {
      setRetranslateError(
        (e.message || '').replace(/^GraphQL error: ?/i, '') ||
          'Translation failed',
      );
    } finally {
      setRetranslating(false);
    }
  };

  const switchView = (next) => {
    if (next === view) return;
    if (next === 'json') {
      const r = rowsToObject(editState.rows);
      if (!r.ok) {
        setViewError(r.error);
        return;
      }
      onEditStateChange({ ...editState, jsonText: JSON.stringify(r.data, null, 2) });
      setViewError(null);
      onViewChange('json');
    } else {
      const r = parseJson(editState.jsonText);
      if (!r.ok) {
        setViewError(`Fix JSON before switching: ${r.error}`);
        return;
      }
      if (!isPlainObject(r.data)) {
        setViewError('Table view only supports JSON objects at the root.');
        return;
      }
      onEditStateChange({ ...editState, rows: objectToRows(r.data) });
      setViewError(null);
      onViewChange('table');
    }
  };

  const handleMarkDelete = () => {
    Modal.confirm({
      title: `Mark locale "${locale}" for deletion?`,
      content:
        'This will not delete it immediately — it will be removed when you click Save changes at the bottom of the page. You can Undo any time before saving.',
      okText: 'Mark for delete',
      okType: 'danger',
      onOk: () => onMarkDelete && onMarkDelete(),
    });
  };

  return (
    <Card bodyStyle={{ padding: 0 }}>
      <div className="locale-editor-toolbar">
        <Space>
          <Tabs
            size="small"
            activeKey={view}
            onChange={switchView}
            items={[
              {
                key: 'table',
                label: (
                  <Space size={4}>
                    <TableOutlined /> Table
                  </Space>
                ),
              },
              {
                key: 'json',
                label: (
                  <Space size={4}>
                    <CodeOutlined /> JSON
                  </Space>
                ),
              },
            ]}
          />
          {isPendingAdd ? (
            <Tag color="orange" style={{ marginLeft: 4 }}>
              New locale — will be created on Save
            </Tag>
          ) : dirty ? (
            <Tag color="orange" style={{ marginLeft: 4 }}>
              Modified — will be saved on Save
            </Tag>
          ) : (
            <span style={{ color: 'rgba(0,0,0,0.55)', fontSize: 12 }}>
              Last saved {formatDate(initialUpdatedAt)}
            </span>
          )}
        </Space>
        <Space>
          {canRetranslate && (
            <Tooltip title="Replace this locale's values with an AI translation of the current en content">
              <Button
                icon={<ThunderboltOutlined />}
                onClick={handleRetranslate}
                loading={retranslating}
              >
                Translate from en
              </Button>
            </Tooltip>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={onReset}
            disabled={!dirty || retranslating}
          >
            Reset
          </Button>
        </Space>
      </div>

      <div className="locale-editor-body">
        {view === 'table' && (
          <KvTable
            rows={editState.rows}
            onChange={(next) => onEditStateChange({ ...editState, rows: next })}
          />
        )}

        {view === 'json' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              {jsonParseError && (
                <Tag color="red" style={{ fontFamily: 'monospace' }}>
                  {jsonParseError}
                </Tag>
              )}
            </div>
            <JsonEditor
              value={editState.jsonText}
              onChange={(next) =>
                onEditStateChange({ ...editState, jsonText: next })
              }
              height={400}
            />
          </>
        )}

        {(viewError || retranslateError) && (
          <Alert
            type="error"
            showIcon
            message={viewError ?? retranslateError}
            style={{ marginTop: 12 }}
            closable
            onClose={() => {
              setViewError(null);
              setRetranslateError(null);
            }}
          />
        )}

        <Divider style={{ margin: '20px 0 12px' }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {isPendingAdd ? (
            <Button icon={<CloseOutlined />} onClick={onDiscardAdd}>
              Discard unsaved locale
            </Button>
          ) : isDefault ? (
            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, fontStyle: 'italic' }}>
              The default <code>en</code> locale cannot be removed — it's the fallback.
            </span>
          ) : (
            <Button danger icon={<DeleteOutlined />} onClick={handleMarkDelete}>
              Mark for delete
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

/* ============================================================== */
/* PendingDeleteView — banner shown when locale is pending delete  */
/* ============================================================== */

const PendingDeleteView = ({ locale, onUndo }) => (
  <Card>
    <Alert
      type="error"
      showIcon
      message={
        <>
          Locale <strong>{locale}</strong> is marked for deletion.
        </>
      }
      description="It will be removed from the server when you click Save changes at the bottom of the page. Click Undo to keep it."
    />
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
      <Button onClick={onUndo}>Undo</Button>
    </div>
  </Card>
);

/* ============================================================== */
/* AddLocalesModal — multi-pick + optional AI translation          */
/* ============================================================== */

const AddLocalesModal = ({ open, onClose, onAdd, excludedCodes }) => {
  const [picked, setPicked] = useState([]);
  const [useAi, setUseAi] = useState(true);

  useEffect(() => {
    if (open) {
      setPicked([]);
      setUseAi(true);
    }
  }, [open]);

  const options = POPULAR_LOCALES.map((l) => ({
    value: l.code,
    label: `${l.code} — ${l.label}`,
    disabled: excludedCodes.includes(l.code),
  }));

  return (
    <Modal
      title="Add locales"
      open={open}
      onCancel={() => onClose(null)}
      width={640}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => onClose(null)}>
          Cancel
        </Button>,
        <Button
          key="add"
          type="primary"
          icon={useAi ? <ThunderboltOutlined /> : <PlusOutlined />}
          onClick={() => onAdd(picked, useAi)}
          disabled={picked.length === 0}
        >
          {picked.length === 0
            ? useAi
              ? 'Translate & add'
              : 'Add as drafts'
            : useAi
              ? `Translate & add ${picked.length} draft${picked.length === 1 ? '' : 's'}`
              : `Add ${picked.length} draft${picked.length === 1 ? '' : 's'}`}
        </Button>,
      ]}
    >
      <p style={{ color: 'rgba(0,0,0,0.55)', marginTop: 0 }}>
        Pick one or more locales — they'll be added as <strong>drafts</strong>.
        Open each in the sidebar and click <strong>Save changes</strong> at the
        bottom to create them on the server.
      </p>

      <div style={{ marginBottom: 12 }}>
        <Select
          mode="tags"
          showSearch
          placeholder="Search or type locale codes (e.g. tr, pt-br)…"
          style={{ width: '100%' }}
          value={picked}
          onChange={(v) =>
            setPicked(
              v
                .map((code) => code.toLowerCase().trim())
                .filter((c) => LOCALE_REGEX.test(c) && !excludedCodes.includes(c)),
            )
          }
          options={options}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          tokenSeparators={[',', ' ']}
          allowClear
        />
      </div>

      <Checkbox
        checked={useAi}
        onChange={(e) => setUseAi(e.target.checked)}
      >
        <Space size={4}>
          <ThunderboltOutlined style={{ color: '#d48806' }} />
          Translate values from <code>en</code> using AI
        </Space>
      </Checkbox>
      <p style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginTop: 4, marginBottom: 0 }}>
        Anthropic Claude translates only string values — keys, structure,
        placeholders and numbers stay intact.
      </p>
    </Modal>
  );
};

/* ============================================================== */
/* RemoteConfigDetail — top-level page                             */
/* ============================================================== */

const RemoteConfigDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery(getRemoteConfig, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });

  const config = data?.getRemoteConfig;

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [activeLocale, setActiveLocale] = useState(null);

  // Per-locale buffers lifted up so locale switches don't drop edits.
  const [editStates, setEditStates] = useState({});
  const [viewByLocale, setViewByLocale] = useState({});
  const [pendingAdds, setPendingAdds] = useState([]); // [{ id, locale, baselineContent }]
  const [pendingDeletes, setPendingDeletes] = useState(new Set());

  const [addLocaleOpen, setAddLocaleOpen] = useState(false);
  const [translatingAll, setTranslatingAll] = useState(false);
  const [translateAllError, setTranslateAllError] = useState(null);

  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState(null);

  const [renameMutation] = useMutation(updateRemoteConfigName, {
    refetchQueries: [getRemoteConfig, getRemoteConfigs],
  });
  const [deleteConfigMutation] = useMutation(deleteRemoteConfig, {
    refetchQueries: [getRemoteConfigs],
  });
  const [upsertLocale] = useMutation(upsertRemoteConfigLocale);
  const [deleteLocale] = useMutation(deleteRemoteConfigLocale);
  const [translateMutation] = useMutation(translateRemoteConfigContent);

  // Initialize edit states for server locales (idempotent — never overwrites
  // existing in-progress buffers).
  useEffect(() => {
    if (!config) return;
    setEditStates((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const l of config.locales) {
        if (!(l.locale in next)) {
          next[l.locale] = makeEditState(l.content);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [config]);

  useEffect(() => {
    if (config && !activeLocale) {
      const en = config.locales.find((l) => l.locale === 'en');
      setActiveLocale(en?.locale ?? config.locales[0]?.locale ?? null);
    }
  }, [config, activeLocale]);

  // -------- helpers --------
  const getBaselineContent = (locale) => {
    const pa = pendingAdds.find((p) => p.locale === locale);
    if (pa) return pa.baselineContent;
    return config?.locales.find((l) => l.locale === locale)?.content ?? {};
  };

  const getView = (locale, baseline) =>
    viewByLocale[locale] ?? (isPlainObject(baseline) ? 'table' : 'json');

  const getLiveContent = (locale) => {
    const es = editStates[locale];
    if (!es) return getBaselineContent(locale);
    const view = getView(locale, getBaselineContent(locale));
    const r = readContent(es, view);
    return r.ok ? r.data : getBaselineContent(locale);
  };

  const isLocaleDirty = (locale) => {
    if (pendingAdds.some((p) => p.locale === locale)) return true;
    if (pendingDeletes.has(locale)) return false;
    const es = editStates[locale];
    if (!es) return false;
    const view = getView(locale, getBaselineContent(locale));
    const current = readContent(es, view);
    if (!current.ok) return true;
    const baseline =
      config?.locales.find((l) => l.locale === locale)?.content ?? {};
    return !deepEqualJson(current.data, baseline);
  };

  // -------- display list --------
  const displayLocales = useMemo(() => {
    if (!config) return [];
    const serverList = config.locales.map((l) => ({
      key: l.id,
      locale: l.locale,
      updatedAt: l.updatedAt,
      status: pendingDeletes.has(l.locale)
        ? 'pending-delete'
        : isLocaleDirty(l.locale)
          ? 'modified'
          : 'saved',
    }));
    const pendingList = pendingAdds.map((p) => ({
      key: p.id,
      locale: p.locale,
      updatedAt: '',
      status: 'pending-add',
    }));
    const en = serverList.find((l) => l.locale === 'en');
    const others = [
      ...serverList.filter((l) => l.locale !== 'en'),
      ...pendingList,
    ].sort((a, b) => a.locale.localeCompare(b.locale));
    return en ? [en, ...others] : others;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, pendingAdds, pendingDeletes, editStates, viewByLocale]);

  const enContent = useMemo(
    () => getLiveContent('en'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, editStates, viewByLocale, pendingAdds],
  );

  const excludedLocaleCodes = useMemo(
    () => [
      'en',
      ...(config?.locales.map((l) => l.locale) ?? []),
      ...pendingAdds.map((p) => p.locale),
    ],
    [config, pendingAdds],
  );

  const draftCounts = useMemo(() => {
    const adds = pendingAdds.length;
    const dels = pendingDeletes.size;
    const mods = config
      ? config.locales.filter(
          (l) => !pendingDeletes.has(l.locale) && isLocaleDirty(l.locale),
        ).length
      : 0;
    return { adds, dels, mods, total: adds + dels + mods };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, pendingAdds, pendingDeletes, editStates, viewByLocale]);

  const hasUnsavedChanges = draftCounts.total > 0;

  if (loading && !config) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Spin />
        </Card>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={{ padding: 24 }}>
        <Card title="Remote Config not found">
          <Button onClick={() => navigate('/remote-configs')}>Back to list</Button>
        </Card>
      </div>
    );
  }

  // -------- header actions --------
  const startRename = () => {
    setNameInput(config.name);
    setEditingName(true);
  };
  const submitRename = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === config.name) {
      setEditingName(false);
      return;
    }
    try {
      await renameMutation({ variables: { id: config.id, name: trimmed } });
      setEditingName(false);
      showSnackBar('success', 'Renamed', `Renamed to "${trimmed}"`);
    } catch (e) {
      showSnackBar('error', 'Error', e.message || 'Rename failed');
    }
  };

  const handleDeleteConfig = () => {
    Modal.confirm({
      title: `Delete Remote Config "${config.name}"?`,
      content:
        'This permanently removes the config and all its locales. Clients will get null for this name. This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteConfigMutation({ variables: { id: config.id } });
          showSnackBar('success', 'Deleted', 'Remote Config deleted');
          navigate('/remote-configs');
        } catch (e) {
          showSnackBar('error', 'Error', e.message || 'Delete failed');
        }
      },
    });
  };

  // -------- per-locale actions --------
  const removePendingAdd = (locale) => {
    setPendingAdds((prev) => prev.filter((p) => p.locale !== locale));
    setEditStates((prev) => {
      const { [locale]: _omit, ...rest } = prev;
      return rest;
    });
    setViewByLocale((prev) => {
      const { [locale]: _omit, ...rest } = prev;
      return rest;
    });
    setActiveLocale('en');
  };

  const markForDelete = (locale) => {
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      next.add(locale);
      return next;
    });
  };

  const undoMarkForDelete = (locale) => {
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      next.delete(locale);
      return next;
    });
  };

  const resetLocale = (locale) => {
    const baseline = getBaselineContent(locale);
    setEditStates((prev) => ({ ...prev, [locale]: makeEditState(baseline) }));
  };

  const applyTranslationToLocale = (locale, content) => {
    setEditStates((prev) => ({ ...prev, [locale]: makeEditState(content) }));
  };

  // -------- add locales (with optional AI translation) --------
  const handleAddLocales = async (codes, useAi) => {
    if (!codes || codes.length === 0) {
      setAddLocaleOpen(false);
      return;
    }
    const contentByLocale = {};
    for (const code of codes) contentByLocale[code] = enContent;

    if (useAi) {
      try {
        const result = await translateMutation({
          variables: {
            sourceContent: enContent,
            sourceLocale: 'en',
            targetLocales: codes,
          },
        });
        const items = result.data?.translateRemoteConfigContent ?? [];
        const failed = [];
        for (const item of items) {
          if (item.error || item.content == null) {
            failed.push(`${item.locale} (${item.error ?? 'no content'})`);
          } else {
            contentByLocale[item.locale] = item.content;
          }
        }
        if (failed.length > 0) {
          showSnackBar(
            'warning',
            'Partial translation',
            `Couldn't translate: ${failed.join('; ')}. Those drafts got the en fallback.`,
          );
        }
      } catch (e) {
        const msg = (e.message || '').replace(/^GraphQL error: ?/i, '');
        showSnackBar('error', 'Translation failed', `${msg}. Locales added with en content.`);
      }
    }

    const newAdds = codes.map((code) => ({
      id: newId(),
      locale: code,
      baselineContent: contentByLocale[code],
    }));
    setPendingAdds((prev) => [...prev, ...newAdds]);
    setEditStates((prev) => {
      const next = { ...prev };
      for (const a of newAdds) next[a.locale] = makeEditState(a.baselineContent);
      return next;
    });
    setAddLocaleOpen(false);
    if (newAdds[0]) setActiveLocale(newAdds[0].locale);
  };

  // -------- Translate all --------
  const handleTranslateAll = async () => {
    setTranslateAllError(null);
    const serverNonEn = config.locales
      .map((l) => l.locale)
      .filter((c) => c !== 'en');
    const adds = pendingAdds.map((p) => p.locale);
    const targets = Array.from(new Set([...serverNonEn, ...adds])).filter(
      (c) => !pendingDeletes.has(c),
    );
    if (targets.length === 0) {
      setTranslateAllError('Nothing to translate — add some locales first.');
      return;
    }
    setTranslatingAll(true);
    try {
      const result = await translateMutation({
        variables: {
          sourceContent: enContent,
          sourceLocale: 'en',
          targetLocales: targets,
        },
      });
      const items = result.data?.translateRemoteConfigContent ?? [];
      const successes = {};
      const failures = [];
      for (const item of items) {
        if (item.error || item.content == null) {
          failures.push(`${item.locale} (${item.error ?? 'no content'})`);
        } else {
          successes[item.locale] = item.content;
        }
      }
      if (Object.keys(successes).length > 0) {
        setEditStates((prev) => {
          const next = { ...prev };
          for (const [locale, content] of Object.entries(successes)) {
            next[locale] = makeEditState(content);
          }
          return next;
        });
        const first = Object.keys(successes)[0];
        if (first) setActiveLocale(first);
      }
      if (failures.length > 0) {
        setTranslateAllError(
          `Couldn't translate ${failures.length} locale${failures.length === 1 ? '' : 's'}: ${failures.join('; ')}`,
        );
      } else {
        showSnackBar('success', 'Translated', `Drafted translations for ${Object.keys(successes).length} locale(s).`);
      }
    } catch (e) {
      setTranslateAllError(
        (e.message || '').replace(/^GraphQL error: ?/i, '') || 'Translation failed',
      );
    } finally {
      setTranslatingAll(false);
    }
  };

  // -------- commit / discard --------
  const commitAll = async () => {
    if (!config) return;
    setCommitError(null);

    const toUpsert = [];
    const seen = new Set();

    for (const p of pendingAdds) {
      const es = editStates[p.locale];
      if (!es) continue;
      const view = getView(p.locale, p.baselineContent);
      const r = readContent(es, view);
      if (!r.ok) {
        setCommitError(`Locale "${p.locale}": ${r.error}`);
        return;
      }
      toUpsert.push({ locale: p.locale, content: r.data });
      seen.add(p.locale);
    }

    for (const l of config.locales) {
      if (pendingDeletes.has(l.locale)) continue;
      if (seen.has(l.locale)) continue;
      if (!isLocaleDirty(l.locale)) continue;
      const es = editStates[l.locale];
      if (!es) continue;
      const view = getView(l.locale, l.content);
      const r = readContent(es, view);
      if (!r.ok) {
        setCommitError(`Locale "${l.locale}": ${r.error}`);
        return;
      }
      toUpsert.push({ locale: l.locale, content: r.data });
    }

    const toDelete = Array.from(pendingDeletes);

    setCommitting(true);
    try {
      for (const item of toUpsert) {
        await upsertLocale({
          variables: {
            remoteConfigId: config.id,
            locale: item.locale,
            content: item.content,
          },
        });
      }
      for (const locale of toDelete) {
        await deleteLocale({
          variables: { remoteConfigId: config.id, locale },
        });
      }
      setPendingAdds([]);
      setPendingDeletes(new Set());
      setEditStates({});
      setViewByLocale({});
      setActiveLocale('en');
      await refetch();
      showSnackBar(
        'success',
        'Saved',
        `${toUpsert.length} upsert${toUpsert.length === 1 ? '' : 's'}, ${toDelete.length} delete${toDelete.length === 1 ? '' : 's'} committed.`,
      );
    } catch (e) {
      setCommitError(
        (e.message || '').replace(/^GraphQL error: ?/i, '') || 'Save failed',
      );
    } finally {
      setCommitting(false);
    }
  };

  const discardAll = () => {
    Modal.confirm({
      title: 'Discard all unsaved changes?',
      content: `${draftCounts.total} change${draftCounts.total === 1 ? '' : 's'} will be lost. This cannot be undone.`,
      okText: 'Discard',
      okType: 'danger',
      onOk: () => {
        setPendingAdds([]);
        setPendingDeletes(new Set());
        setEditStates({});
        setViewByLocale({});
        setActiveLocale('en');
        setCommitError(null);
        setTranslateAllError(null);
      },
    });
  };

  const active = displayLocales.find((l) => l.locale === activeLocale);
  const activeBaseline = active ? getBaselineContent(active.locale) : {};
  const activeView = active ? getView(active.locale, activeBaseline) : 'table';
  const activeEditState =
    active && editStates[active.locale]
      ? editStates[active.locale]
      : makeEditState(activeBaseline);

  return (
    <div className="remote-config-detail-container">
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/remote-configs')}
        style={{ marginBottom: 12 }}
      >
        Back to list
      </Button>

      <Card style={{ marginBottom: 16 }}>
        <div className="rc-header">
          <div className="rc-header-info">
            <GlobalOutlined style={{ fontSize: 22 }} />
            {editingName ? (
              <Space>
                <Input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  style={{ width: 320 }}
                  autoFocus
                  onPressEnter={submitRename}
                />
                <Button type="primary" icon={<CheckOutlined />} onClick={submitRename} />
                <Button icon={<CloseOutlined />} onClick={() => setEditingName(false)} />
              </Space>
            ) : (
              <Space>
                <h1>{config.name}</h1>
                <Button type="text" icon={<EditOutlined />} onClick={startRename} />
              </Space>
            )}
          </div>
          <Space>
            <Tooltip title="Re-translate every non-en locale from the current en content using AI. Results stay as drafts until you Save changes.">
              <Button
                icon={<ThunderboltOutlined />}
                onClick={handleTranslateAll}
                loading={translatingAll}
                disabled={
                  config.locales.filter((l) => l.locale !== 'en').length === 0 &&
                  pendingAdds.length === 0
                }
              >
                Translate all
              </Button>
            </Tooltip>
            <Button danger icon={<DeleteOutlined />} onClick={handleDeleteConfig}>
              Delete Config
            </Button>
          </Space>
        </div>
        <div className="rc-header-meta">
          <span>Created {formatDate(config.createdAt)}</span>
          <span>Updated {formatDate(config.updatedAt)}</span>
          <span style={{ fontFamily: 'monospace' }}>
            {displayLocales.length} locale{displayLocales.length === 1 ? '' : 's'}
          </span>
        </div>
        {translateAllError && (
          <Alert
            type="error"
            showIcon
            message={translateAllError}
            style={{ marginTop: 12 }}
            closable
            onClose={() => setTranslateAllError(null)}
          />
        )}
      </Card>

      <div className="rc-layout">
        <aside className="rc-sidebar">
          <Card
            size="small"
            title={
              <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>
                LOCALES · {displayLocales.length}
                {draftCounts.total > 0 && (
                  <span style={{ marginLeft: 6, color: '#d48806', fontWeight: 500 }}>
                    ({draftCounts.total} unsaved)
                  </span>
                )}
              </span>
            }
            extra={
              <Tooltip title="Add locales">
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => setAddLocaleOpen(true)}
                />
              </Tooltip>
            }
            bodyStyle={{ padding: 0 }}
          >
            <ul className="rc-locale-list">
              {displayLocales.map((l) => {
                const isActive = l.locale === activeLocale;
                return (
                  <li key={l.key}>
                    <button
                      type="button"
                      className={`rc-locale-item${isActive ? ' active' : ''}`}
                      onClick={() => setActiveLocale(l.locale)}
                    >
                      <div className="rc-locale-row">
                        <span
                          className="rc-locale-code"
                          style={{
                            textDecoration:
                              l.status === 'pending-delete' ? 'line-through' : 'none',
                          }}
                        >
                          {l.locale}
                        </span>
                        {getLocaleLabel(l.locale) && (
                          <span className="rc-locale-label">
                            {getLocaleLabel(l.locale)}
                          </span>
                        )}
                        {l.locale === 'en' && (
                          <Tag style={{ marginLeft: 'auto', fontSize: 10 }} bordered={false}>
                            default
                          </Tag>
                        )}
                        {l.status === 'pending-add' && (
                          <Tag color="orange" style={{ fontSize: 10 }} bordered={false}>
                            draft
                          </Tag>
                        )}
                        {l.status === 'modified' && (
                          <Tag color="orange" style={{ fontSize: 10 }} bordered={false}>
                            modified
                          </Tag>
                        )}
                        {l.status === 'pending-delete' && (
                          <Tag color="red" style={{ fontSize: 10 }} bordered={false}>
                            pending delete
                          </Tag>
                        )}
                      </div>
                      <div className="rc-locale-meta">
                        {l.status === 'pending-add'
                          ? 'New locale — pending save'
                          : l.status === 'pending-delete'
                            ? 'Marked for deletion'
                            : l.status === 'modified'
                              ? 'Has unsaved edits'
                              : `Updated ${formatDate(l.updatedAt)}`}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        </aside>

        <main className="rc-main" style={{ paddingBottom: hasUnsavedChanges ? 80 : 0 }}>
          {active && active.status === 'pending-delete' && (
            <PendingDeleteView
              locale={active.locale}
              onUndo={() => undoMarkForDelete(active.locale)}
            />
          )}
          {active && active.status !== 'pending-delete' && (
            <LocaleEditor
              locale={active.locale}
              editState={activeEditState}
              onEditStateChange={(next) =>
                setEditStates((prev) => ({ ...prev, [active.locale]: next }))
              }
              view={activeView}
              onViewChange={(next) =>
                setViewByLocale((prev) => ({ ...prev, [active.locale]: next }))
              }
              enContent={enContent}
              status={active.status}
              initialUpdatedAt={active.updatedAt}
              dirty={
                active.status === 'modified' || active.status === 'pending-add'
              }
              onReset={() => resetLocale(active.locale)}
              onMarkDelete={() => markForDelete(active.locale)}
              onDiscardAdd={() => removePendingAdd(active.locale)}
              onApplyTranslation={(content) =>
                applyTranslationToLocale(active.locale, content)
              }
            />
          )}
        </main>
      </div>

      <AddLocalesModal
        open={addLocaleOpen}
        onClose={() => setAddLocaleOpen(false)}
        onAdd={handleAddLocales}
        excludedCodes={excludedLocaleCodes}
      />

      {/* ---- Sticky bottom action bar ---- */}
      {hasUnsavedChanges && (
        <div className="rc-action-bar">
          <div className="rc-action-bar-inner">
            <div>
              <Tag color="orange" style={{ fontWeight: 500 }}>
                {draftCounts.total} unsaved change
                {draftCounts.total === 1 ? '' : 's'}
              </Tag>
              <span style={{ color: 'rgba(0,0,0,0.55)', fontSize: 12, marginLeft: 8 }}>
                {[
                  draftCounts.adds > 0 && `+${draftCounts.adds} new`,
                  draftCounts.mods > 0 && `${draftCounts.mods} modified`,
                  draftCounts.dels > 0 && `−${draftCounts.dels} delete${draftCounts.dels === 1 ? '' : 's'}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {commitError && (
                <span style={{ color: '#cf1322', fontSize: 12, marginLeft: 12 }}>
                  {commitError}
                </span>
              )}
            </div>
            <Space>
              <Button onClick={discardAll} disabled={committing}>
                Discard changes
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={commitAll}
                loading={committing}
              >
                Save changes
              </Button>
            </Space>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemoteConfigDetail;
